import React, { useMemo } from "react";
import axios from "axios";
import {
  Descriptions,
  Form,
  Image,
  InputNumber,
  Modal,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography,
  Alert,
  message,
} from "antd";
import { PlayCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Constants from "../Utils/Constants";

const isMinioKey = (value, prefix) =>
  typeof value === "string" && value.startsWith(prefix);

const normalizeLegacyUrl = (url) => {
  if (!url) return null;

  if (url.startsWith("http")) {
    return url;
  }

  return `${Constants.SERVER_URL.replace(/\/$/, "")}/${url.replace(
    /^\/+/,
    ""
  )}`;
};

export default function ExerciseDetailsModal({
  open,
  exerciseKey,
  exercise,
  onClose,
  onAdd,
}) {
  const { t, i18n } = useTranslation("ExerciseDetailsModal");
  const token = localStorage.getItem("token");

  const headers = useMemo(
    () => ({
      Authorization: "Bearer " + token,
      "Accept-Language": i18n.language,
    }),
    [token, i18n.language]
  );

  const [form] = Form.useForm();

  const [data, setData] = React.useState(exercise || null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const [imagePreviewUrl, setImagePreviewUrl] = React.useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = React.useState(null);

  const lvlKey = (value) => `level_${String(value || "").toLowerCase()}`;
  const labelLvl = (value) => t(lvlKey(value), { defaultValue: value });

  const getSignedUrl = async (key, bucket) => {
    const res = await axios.get(
      `${Constants.SERVER_URL}/api/minio/presign-download`,
      {
        params: {
          bucket,
          key,
        },
        headers,
      }
    );

    return res.data.downloadUrl;
  };

  React.useEffect(() => {
    setData(exercise || null);
  }, [exercise]);

  React.useEffect(() => {
    if (!open) return;

    if (exerciseKey && !exercise) {
      setLoading(true);
      setErr(null);

      axios
        .get(
          `${Constants.SERVER_URL}/exercise/${encodeURIComponent(
            exerciseKey
          )}`,
          {
            headers,
          }
        )
        .then((res) => {
          setData(res.data);
        })
        .catch((error) => {
          setErr(error);

          const raw =
            error?.response?.data?.message ||
            error?.message ||
            "no_exercise_endpoint";

          message.warning(
            t(`Backend:${raw}`) || "Endpoint /exercise/:exerciseKey indisponible"
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, exerciseKey, exercise, headers, t]);

  React.useEffect(() => {
    if (!data) {
      setImagePreviewUrl(null);
      setVideoPreviewUrl(null);
      return;
    }

    let cancelled = false;

    async function loadMedia() {
      try {
        if (data.imageUrl) {
          if (isMinioKey(data.imageUrl, "images/")) {
            const signedImageUrl = await getSignedUrl(
              data.imageUrl,
              "rxapa-assets"
            );

            if (!cancelled) {
              setImagePreviewUrl(signedImageUrl);
            }
          } else if (!cancelled) {
            setImagePreviewUrl(normalizeLegacyUrl(data.imageUrl));
          }
        } else if (!cancelled) {
          setImagePreviewUrl(null);
        }

        if (data.videoUrl) {
          if (isMinioKey(data.videoUrl, "videos/")) {
            const signedVideoUrl = await getSignedUrl(
              data.videoUrl,
              "rxapa-videos"
            );

            if (!cancelled) {
              setVideoPreviewUrl(signedVideoUrl);
            }
          } else if (!cancelled) {
            setVideoPreviewUrl(normalizeLegacyUrl(data.videoUrl));
          }
        } else if (!cancelled) {
          setVideoPreviewUrl(null);
        }
      } catch {
        if (!cancelled) {
          setImagePreviewUrl(null);
          setVideoPreviewUrl(null);
        }
      }
    }

    loadMedia();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const initialValues = useMemo(
    () => ({
      series: 3,
      repetitions: data?.numberOfRepetitions ?? 10,
      minutes: 0,
      rest: 60,
      required: true,
    }),
    [data?.numberOfRepetitions]
  );

  React.useEffect(() => {
    if (!open || !data) return;

    form.setFieldsValue({
      series: 3,
      repetitions: data?.numberOfRepetitions ?? 10,
      minutes: 0,
      rest: 60,
      required: true,
    });
  }, [open, data, form]);

  const submitAdd = () => {
    const params = form.getFieldsValue(true);

    onAdd?.(data, {
      series: Number(params.series ?? 3),
      repetitions: Number(
        params.repetitions ?? data?.numberOfRepetitions ?? 10
      ),
      minutes: Number(params.minutes ?? 0),
      rest: Number(params.rest ?? 60),
      required: Boolean(params.required ?? true),
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={submitAdd}
      okText={t("label_add_to_bloc") || "Ajouter au bloc"}
      cancelText={t("label_close") || "Fermer"}
      title={
        data?.name
          ? `${t("label_exercise") || "Exercice"} : ${data.name}`
          : t("label_exercise_details") || "Détails de l'exercice"
      }
      destroyOnClose
      width={760}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : err ? (
        <Alert
          type="error"
          message={
            t("label_error_loading") ||
            "Erreur lors du chargement de l'exercice."
          }
        />
      ) : !data ? (
        <Alert
          type="warning"
          message={t("label_exercise_not_found") || "Exercice introuvable."}
        />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {imagePreviewUrl && (
            <Image
              src={imagePreviewUrl}
              alt={data.name}
              height={220}
              style={{
                objectFit: "cover",
                borderRadius: 8,
              }}
              preview
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          )}

          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label={t("label_name") || "Nom"}>
              {data.name || "—"}
            </Descriptions.Item>

            <Descriptions.Item label={t("label_level") || "Niveau"}>
              {labelLvl(data.level ?? data.fitnessLevel) || "—"}
            </Descriptions.Item>

            <Descriptions.Item label={t("label_category") || "Catégorie"}>
              {data.category || "—"}
            </Descriptions.Item>

            {data?.numberOfRepetitions != null && (
              <Descriptions.Item
                label={t("label_reps") || "Répétitions"}
              >
                {data.numberOfRepetitions}
              </Descriptions.Item>
            )}

            {Array.isArray(data?.muscles) && (
              <Descriptions.Item
                label={t("label_muscles") || "Muscles"}
                span={2}
              >
                <Space wrap>
                  {data.muscles.map((muscle) => (
                    <Tag key={muscle}>{muscle}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}

            {videoPreviewUrl && (
              <Descriptions.Item label="Vidéo" span={2}>
                <Typography.Link
                  href={videoPreviewUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <PlayCircleOutlined style={{ marginRight: 6 }} />
                  {t("label_view_video") || "Voir la vidéo"}
                </Typography.Link>
              </Descriptions.Item>
            )}
          </Descriptions>

          {data?.description && (
            <Typography.Paragraph>{data.description}</Typography.Paragraph>
          )}

          <Form
            layout="inline"
            form={form}
            initialValues={initialValues}
          >
            <Form.Item
              label={t("label_series") || "Séries"}
              name="series"
              rules={[{ type: "number", min: 1 }]}
            >
              <InputNumber min={1} max={20} />
            </Form.Item>

            <Form.Item
              label={t("label_reps") || "Répétitions"}
              name="repetitions"
              rules={[{ type: "number", min: 1 }]}
            >
              <InputNumber min={1} max={200} />
            </Form.Item>

            <Form.Item
              label={t("label_minutes") || "Minutes"}
              name="minutes"
              rules={[{ type: "number", min: 0 }]}
            >
              <InputNumber min={0} max={180} />
            </Form.Item>

            <Form.Item
              label={`${t("label_rest") || "Repos"} (s)`}
              name="rest"
              rules={[{ type: "number", min: 0 }]}
            >
              <InputNumber min={0} max={600} />
            </Form.Item>

            <Form.Item
              label={t("label_required") || "Obligatoire"}
              name="required"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Space>
      )}
    </Modal>
  );
}